import * as RadixPopover from "@radix-ui/react-popover";
import React, { useRef, useState } from "react";

type Side = "top" | "right" | "bottom" | "left";
type Align = "start" | "end";
export type Placement = Side | `${Side}-${Align}`;

const matchesFocusVisible = (element: Element) => {
  try {
    return element.matches(":focus-visible");
  } catch {
    return true;
  }
};

const tabbables = (container: Element) =>
  Array.from(container.querySelectorAll<HTMLElement>("*")).filter(
    (element) => element.tabIndex >= 0 && !element.matches(":disabled"),
  );

/**
 * Radix loops Tab inside the popup. Stopping the key at the popup's edges hands it back to the
 * browser, so Tab leaves the popup, which then closes on losing focus.
 */
const letTabLeave = (event: React.KeyboardEvent<HTMLElement>) => {
  if (event.key !== "Tab") {
    return;
  }
  const stops = tabbables(event.currentTarget);
  const edge =
    stops.length === 0 ? event.currentTarget : event.shiftKey ? stops[0] : stops[stops.length - 1];
  if (document.activeElement === edge) {
    event.stopPropagation();
  }
};

export interface PopoverProps {
  className?: string;
  /** Accessible name of the popup, which a dialog needs when nothing inside it titles it. */
  label?: string;
  onClose?: () => void;
  placement?: Placement;
  /** "menu" for a list of actions; the default "dialog" suits anything else. */
  role?: "dialog" | "menu";
  /** Keyboard focus on the trigger opens the popup; a menu waits for Enter or Space instead. */
  openOnFocus?: boolean;
  /** ArrowDown on the trigger opens the popup too, as it does on a native select. */
  openOnArrowDown?: boolean;
  renderContent: (close: () => void, isOpen: boolean) => React.ReactNode;
  /** `isOpen` lets a trigger that is itself a button carry `aria-expanded`. */
  renderTrigger: (isOpen: boolean) => React.ReactNode;
}
export const Popover = (props: PopoverProps) => {
  const {
    className,
    label,
    placement = "bottom",
    role = "dialog",
    openOnFocus = true,
    openOnArrowDown = false,
    onClose,
    renderContent,
    renderTrigger,
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  // A click or focus outside closes the popup and keeps focus where it went
  const interactedOutside = useRef(false);
  // Focus returning to the trigger must not reopen the popup it closed
  const returningFocus = useRef(false);
  const [side, align = "center"] = placement.split("-") as [Side, Align | undefined];

  const close = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className={className}>
      <RadixPopover.Root open={isOpen} onOpenChange={setIsOpen}>
        <RadixPopover.Trigger asChild aria-haspopup={role}>
          <div
            ref={triggerRef}
            className="popover-trigger relative flex w-full items-center justify-center border-none bg-transparent p-0 text-left"
            onFocus={(event) => {
              if (openOnFocus && !returningFocus.current && matchesFocusVisible(event.target)) {
                setIsOpen(true);
              }
            }}
            onKeyDown={(event) => {
              if (openOnArrowDown && !isOpen && event.key === "ArrowDown") {
                event.preventDefault();
                setIsOpen(true);
              }
            }}
          >
            {renderTrigger(isOpen)}
          </div>
        </RadixPopover.Trigger>
        {isOpen && (
          <RadixPopover.Content
            asChild
            side={side}
            align={align}
            sideOffset={10}
            role={role}
            aria-label={label}
            onKeyDownCapture={letTabLeave}
            onOpenAutoFocus={(event) => {
              // Radix passes over links, and the account menu's first items are links
              event.preventDefault();
              const popup = event.currentTarget as HTMLElement;
              (tabbables(popup)[0] ?? popup).focus();
            }}
            onInteractOutside={(event) => {
              if (!triggerRef.current?.contains(event.target as Node)) {
                interactedOutside.current = true;
              }
            }}
            onCloseAutoFocus={(event) => {
              // Radix focuses the wrapper, which takes no focus; the button inside it should
              event.preventDefault();
              if (!interactedOutside.current && triggerRef.current) {
                returningFocus.current = true;
                tabbables(triggerRef.current)[0]?.focus();
                returningFocus.current = false;
              }
              interactedOutside.current = false;
            }}
          >
            {/* Callers restyle the surface through `.popover-content`, which outranks these classes */}
            <div className="popover-content z-100 min-w-[260px] rounded-sm bg-background px-4 py-3 text-sm text-foreground">
              {renderContent(close, isOpen)}
            </div>
          </RadixPopover.Content>
        )}
      </RadixPopover.Root>
    </div>
  );
};
